import { EMPTY_UID, InviteCodeData } from './../types/core';
import { atomWithIndexedPersistence, atomWithPersistence } from "../utils/persistence";
import { Company } from '../types/company';
import { Billing, Delinquency } from "../types/billing";
import { SupportSessionMeta } from "../types/reseller";
import { atom } from "recoil";
import { selector, selectorFamily } from "./recoilSelectors";
import { pageState } from "./appState";
import { DAY, HOUR } from "../utils/time";
import { isAdmin, isOperator, isWorker, Timestamps } from "../types/member";
import {
    getBrowserDistanceFormat,
    getBrowserLanguage,
    getBrowserTimeFormat,
    getBrowserTimezone,
    getLanguageFromLocale
} from '../utils/browser';
import { isLocationOnlyPlan } from '../utils/account';

export const passwordState = atom({ key: "account.password", default: "" });
export const emailVerifiedState = atomWithPersistence("account.emailVerified", false);
export const surveyDoneState = atomWithPersistence("account.surveyDone", false);
export const hardLoginTsState = atomWithPersistence("account.hardLoginTs", 0);
export const usernameState = atomWithPersistence("account.username", "");
export const loggedInState = atomWithPersistence("account.loggedIn", false);
export const loginTsState = atomWithPersistence("account.loginTs", 0);
export const uidState = atomWithPersistence("account.uid", EMPTY_UID);
export const iconState = atomWithPersistence("account.icon", "");
export const nameState = atomWithPersistence("account.name", "");
export const roleState = atomWithPersistence("account.role", "");
export const timeFormatState = atomWithPersistence("account.timeFormat", "24");
export const distanceFormatState = atomWithPersistence("account.distanceFormat", "SI");
export const languageState = atomWithPersistence("account.language", "");
export const timezoneState = atomWithPersistence("account.timezone", "");
export const dangerousCompanyState = atomWithPersistence<Company | undefined>("company", undefined);
export const inviteCodesState = atomWithIndexedPersistence<InviteCodeData[]>("invite.codes", []);
export const billingState = atomWithPersistence<Billing | undefined>("account.billing", undefined);
export const timestampsState = atomWithPersistence<Timestamps | undefined>("account.timestamps", undefined);

export const tokenState = atomWithPersistence("account.token", "");
export const accessState = atomWithPersistence("account.access", "");
export const accountKeyState = atomWithPersistence("account.key", "");
export const companyKeyState = atomWithPersistence("company.key", "");
export const businessState = atomWithPersistence("account.isBusiness", false);
export const companyUsernameState = atomWithPersistence("company.username", "");
export const slackTeamIdState = atomWithPersistence("slack.teamid", "");
export const unblockTsState = atomWithPersistence("unblock.ts", 0);
export const paymentMethodReminderTsState = atomWithPersistence("paymentmethod.reminder.ts", 0);
export const delinquencyReminderTsState = atomWithPersistence("delinquency.reminder.ts", 0);
export const delinquencyFirstSeenTsState = atomWithPersistence("delinquency.firstseen.ts", 0);
export const supportSessionState = atomWithPersistence<SupportSessionMeta | undefined>("account.supportSession", undefined);
/** The one-time hints seen in this browser; kept on logout, so logging in again doesn't bring them back. */
export const seenHintsState = atomWithPersistence<string[]>("app.seenhints", []);

// escalation ladder for unpaid invoices, in days since the oldest open invoice
export const DELINQUENCY_MODAL_DAYS = 7;
export const DELINQUENCY_MODAL_EVERY_LOAD_DAYS = 21;
export const DELINQUENCY_BLOCK_DAYS = 30;
// accounts already 30+ days delinquent when this ships still get a warning runway
export const DELINQUENCY_GRACE_DAYS = 7;

export const delinquencyState = selector<Delinquency | undefined>({
    key: "account.delinquency",
    get: ({ get }) => get(billingState)?.delinquency
});

export const delinquencyDaysState = selector<number>({
    key: "account.delinquency.days",
    get: ({ get }) => {
        const since = get(delinquencyState)?.since;
        if (!since) {
            return 0;
        }
        return Math.floor((Date.now() - since * 1000) / DAY);
    }
});

// when dashboard access gets blocked: 30 days after the oldest open invoice, but never
// less than 7 days after this browser first saw the account delinquent (grace runway
// for accounts that are already months overdue when the feature ships)
export const delinquencyBlockTsState = selector<number>({
    key: "account.delinquency.blockTs",
    get: ({ get }) => {
        const delinquency = get(delinquencyState);
        if (!delinquency) {
            return 0;
        }
        const firstSeen = get(delinquencyFirstSeenTsState) || Date.now();
        return Math.max(
            delinquency.since * 1000 + DELINQUENCY_BLOCK_DAYS * DAY,
            firstSeen + DELINQUENCY_GRACE_DAYS * DAY
        );
    }
});

export const accountBlockedState = selector<boolean>({
    key: "account.blocked",
    get: ({ get }) => {
        const billing = get(billingState);
        const customer = billing?.customerState?.state;
        const unblocked = Date.now() < get(unblockTsState);
        const delinquencyBlockTs = get(delinquencyBlockTsState);
        const blocked = (customer === "no_card" && !!billing?.subscription)
            || (delinquencyBlockTs > 0 && Date.now() >= delinquencyBlockTs);
        if (blocked) {
            get(pageState); // this will reevaluate to blocked state on page changes
        }
        return blocked && !unblocked;
    }
});

/** The trial ran out without a card: owners and admins choose their plan in the plan window instead of company settings. */
export const trialEndedForAdminState = selector<boolean>({
    key: "account.trial.ended.admin",
    get: ({ get }) => {
        const billing = get(billingState);
        return billing?.customerState?.state === "no_card" && !!billing?.subscription && get(isSelfAdminOrCompanyState);
    }
});

/** The phone layout's trial reminder; the dashboard asks with the plan window. */
export const showPaymentMethodReminderState = selector<boolean>({
    key: "paymentmethod.reminder.show",
    get: ({ get }) => {
        const billing = get(billingState);
        const customer = billing?.customerState?.state;
        const reminderTs = get(paymentMethodReminderTsState);
        if (customer === "trialing"
            && billing?.subscription?.trialEnd
            && billing?.subscription.trialEnd * 1000 < Date.now() + 14 * DAY
            && reminderTs < Date.now() - 48 * HOUR
        ) {
            return true;
        }
        return false;
    }
});

export const companyFieldState = selectorFamily<any, keyof Company>({
    key: "account.company.field",
    get: (field: string) => ({ get }) => {
        const company = get(dangerousCompanyState);
        if (company && Object.keys(company).includes(field)) {
            return (company as {[key: string]: any})[field];
        }
        return undefined;
    }
});

export const isAmPmState = selector({
    key: "account.isAmPm",
    get: ({ get }) => {
        return get(timeFormatState) === '12';
    }
});
export const isImperialState = selector({
    key: "account.isImperial",
    get: ({ get }) => {
        return get(distanceFormatState) !== 'SI';
    }
});
export const isSelfCompanyState = selector({
    key: "account.isSelfCompany",
    get: ({ get }) => {
        return get(companyFieldState("uid")) === get(uidState);
    }
});
export const isSelfWorkerState = selector({
    key: "account.isSelfWorker",
    get: ({ get }) => {
        return !get(isSelfCompanyState) && isWorker(get(roleState));
    }
});
export const isSelfOperatorState = selector({
    key: "account.isSelfOperator",
    get: ({ get }) => {
        return !get(isSelfCompanyState) && isOperator(get(roleState));
    }
});
export const isSelfAdminState = selector({
    key: "account.isSelfAdmin",
    get: ({ get }) => {
        return !get(isSelfCompanyState) && isAdmin(get(roleState));
    }
});
export const isSelfAdminOrCompanyState = selector({
    key: "account.isSelfAdminOrCompany",
    get: ({ get }) => {
        return get(isSelfAdminState) || get(isSelfCompanyState);
    }
});
export const hasCompanyState = selector({
    key: "account.hasCompany",
    get: ({ get }) => {
        return !!get(companyFieldState("uid"));
    }
});
export const timezoneMatchesBrowserState = selector({
    key: "account.timezoneMatchesBrowser",
    get: ({ get }) => {
        return get(timezoneState) === getBrowserTimezone();
    }
});
export const timeFormatMatchesBrowserState = selector({
    key: "account.timeFormatMatchesBrowser",
    get: ({ get }) => {
        return get(timeFormatState) === getBrowserTimeFormat();
    }
});
export const distanceFormatMatchesBrowserState = selector({
    key: "account.distanceFormatMatchesBrowser",
    get: ({ get }) => {
        return get(distanceFormatState) === getBrowserDistanceFormat();
    }
});
export const languageMatchesBrowserState = selector({
    key: "account.languageMatchesBrowser",
    get: ({ get }) => {
        return getLanguageFromLocale(get(languageState)) === getLanguageFromLocale(getBrowserLanguage());
    }
});
export const isLocationPlanState = selector({
    key: "account.isLocationPlan",
    get: ({ get }) => {
        return isLocationOnlyPlan(get(companyFieldState("stripe_plan")));
    }
});
export const isDispatchPlanState = selector({
    key: "account.isDispatchPlan",
    get: ({ get }) => {
        return !get(isLocationPlanState);
    }
});
